import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ProjectStack.css';

const ProjectStack = ({ projects = [], maxVisible = 3 }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
  };

  const closeModal = () => {
    setSelectedProject(null);
  };

  return (
    <div className="project-stack">
      <div className="project-stack__container">
        {projects.slice(0, maxVisible).map((project, index) => {
          const isHovered = hoveredIndex === index;
          const stackOffset = index * 8;
          const rotateOffset = (index - Math.floor(maxVisible / 2)) * 2;
          
          return (
            <motion.div
              key={project.id}
              className="project-stack__card"
              style={{
                zIndex: maxVisible - index,
              }}
              initial={{
                y: stackOffset,
                rotate: rotateOffset,
                scale: 1 - (index * 0.05),
              }}
              animate={{
                y: isHovered ? stackOffset - 20 : stackOffset,
                rotate: isHovered ? 0 : rotateOffset,
                scale: isHovered ? 1 : 1 - (index * 0.05),
                x: isHovered ? index * 10 : 0,
              }}
              whileHover={{
                y: stackOffset - 30,
                scale: 1.05,
                rotate: 0,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
              }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              onClick={() => handleProjectClick(project)}
            >
              <div className="project-stack__card-inner">
                <div className="project-stack__cover">
                  <img 
                    src={project.cover} 
                    alt={project.title}
                    onError={(e) => {
                      e.target.src = '/api/placeholder/300/300';
                    }}
                  />
                  <div className="project-stack__overlay">
                    <div className="project-stack__play-icon">
                      <svg viewBox="0 0 24 24" width="24" height="24">
                        <path fill="currentColor" d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
                
                <div className="project-stack__info">
                  <h3 className="project-stack__title">{project.title}</h3>
                  <p className="project-stack__type">{project.type}</p>
                  <div className="project-stack__meta">
                    <span className="project-stack__year">{project.year}</span>
                    <span className="project-stack__tracks">
                      {project.tracks?.length || 0} tracks
                    </span>
                  </div>
                </div>
                
                <div className="project-stack__system-info">
                  <div className="project-stack__status">
                    <span className="project-stack__status-dot"></span>
                    READY
                  </div>
                  <div className="project-stack__id">
                    ID: {project.id.toString().padStart(4, '0')}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="project-stack__modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="project-stack__modal"
              initial={{ 
                scale: 0.8, 
                opacity: 0,
                rotateX: -15 
              }}
              animate={{ 
                scale: 1, 
                opacity: 1,
                rotateX: 0 
              }}
              exit={{ 
                scale: 0.8, 
                opacity: 0,
                rotateX: 15 
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="project-stack__close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
              
              <div className="project-stack__modal-content">
                <div className="project-stack__modal-header">
                  <img 
                    src={selectedProject.cover} 
                    alt={selectedProject.title}
                    className="project-stack__modal-cover"
                  />
                  <div className="project-stack__modal-info">
                    <h2>{selectedProject.title}</h2>
                    <p className="project-stack__modal-type">{selectedProject.type}</p>
                    <p className="project-stack__modal-description">
                      {selectedProject.description}
                    </p>
                    <div className="project-stack__modal-meta">
                      <span>Released: {selectedProject.year}</span>
                      <span>Tracks: {selectedProject.tracks?.length || 0}</span>
                      <span>Duration: {selectedProject.duration}</span>
                    </div>
                  </div>
                </div>
                
                {selectedProject.tracks && (
                  <div className="project-stack__modal-tracklist">
                    <h3>Tracklist</h3>
                    <div className="project-stack__tracks">
                      {selectedProject.tracks.map((track, index) => (
                        <div key={track.id} className="project-stack__track">
                          <span className="project-stack__track-number">
                            {(index + 1).toString().padStart(2, '0')}
                          </span>
                          <span className="project-stack__track-title">
                            {track.title}
                          </span>
                          <span className="project-stack__track-duration">
                            {track.duration}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectStack;
